import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SltKeySetupService } from '../../services/slt-key-setup.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-slt-key-setup',
    templateUrl: './slt-key-setup.component.html',
    styleUrls: ['./slt-key-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltKeySetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'Submission/Key Setup';
  minimise = false;
  loading = false;
  noRecords = false;
  isResyncClicked = false;

  // Component-specific properties
  sltKeySetupData: any[] = [];
  totalRecords = 0;
  clientKey: any;

  // Global settings
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private sltKeySetupService: SltKeySetupService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    this.clientKey =
      (header as any).client_key ??
      header.globalParams?.client?.client_key ??
      header.globalParams?.client_key ??
      header.userSettingsData?.user?.client_key;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Load SLT key setup data
        this.getKeySetupData();
      }
    } catch (e) {
      console.error('Error in SltKeySetup init:', e);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get SLT Key Setup data from API
   */
  getKeySetupData() {
    this.loading = true;
    this.noRecords = false;
    this.cdr.detectChanges();

    // Use real data from rowData (NO hardcoded values)
    this.sltKeySetupService.getKeySetupData(
      this.GENERAL_CONFIG.base_url,
      this.rowData.TAX_YEAR,
      this.rowData.MEF_GROUP_KEY
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === "1") {
          if (response.jsonObject && response.jsonObject !== null && response.jsonObject.length > 0) {
            this.sltKeySetupData = response.jsonObject;
            this.totalRecords = this.sltKeySetupData.length;
            this.noRecords = false;
          } else {
            this.sltKeySetupData = [];
            this.totalRecords = 0;
            this.noRecords = true;
          }

          this.loading = false;
          this.cdr.detectChanges();
        } else {
          this.loading = false;
          this.noRecords = true;

          // Show error message if available
          if (response.message) {
            this._snackBar.open(response.message, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }

          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        this.noRecords = true;
        console.error('Error loading SLT key setup data:', error);

        this._snackBar.open('Error loading key setup data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });

        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Re-sync group data
   */
  resync() {
    this.isResyncClicked = true;
    this.loading = true;
    this.cdr.detectChanges();

    // Use real data from rowData (NO hardcoded values)
    this.sltKeySetupService.reSyncGroup(
      this.GENERAL_CONFIG.base_url,
      this.rowData.TAX_YEAR,
      this.rowData.MEF_GROUP_KEY
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isResyncClicked = false;

        if (response.callSuccess === "1") {
          this.loading = false;

          this._snackBar.open('Re-sync Group Execute Successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          // Update data with response
          if (response.jsonObject && response.jsonObject.length > 0) {
            this.sltKeySetupData = response.jsonObject;
            this.totalRecords = this.sltKeySetupData.length;
            this.noRecords = false;
          }

          this.cdr.detectChanges();
        } else {
          this.loading = false;

          // Show error message if available
          const errorMsg = response.message || 'Error occurred during re-sync. Please try again.';
          this._snackBar.open(errorMsg, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });

          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.isResyncClicked = false;
        this.loading = false;
        console.error('Error during re-sync:', error);

        this._snackBar.open('Error occurred during re-sync. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });

        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
